// External libraries
import React from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { func } from 'prop-types';
import { LoadingElement, PhoneInput } from '@checkout/react-components';

// Internal modules
import { features, getContent } from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import { phoneInputStyleOverrides } from './styles';
import {
  CupOtpMessage,
  DialingCodeSelect,
  PhoneConfirmationText,
  PhoneContainer,
  PhoneType,
} from './Components';

const Phone = props => {
  useMountLogging('Phone');
  const isAllowChangePhoneType = features.isActive('allowChangePhoneType');
  const canDialingCodeChange = features.isActive('canDialingCodeChange');
  const shouldShowSCAPhoneConfirmation = features.isActive(
    'shouldShowSCAPhoneConfirmation',
  );
  const dialingCode = useStoreState(({ form }) => form.dialingCode);
  const phone = useStoreState(({ form }) => form.phone);
  const phoneType = useStoreState(({ form }) => form.phoneType);
  const cardType = useStoreState(({ form }) => form.cardNumber.cardType);
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const hasCupOneTimePasswordConfirmationMessage = useStoreState(
    ({ ui }) => ui.hasCupOneTimePasswordConfirmationMessage,
  );
  const {
    dialingCodeSelectOptions,
    phoneMeta,
    territoriesToDialingCodeMap,
  } = useStoreState(({ api }) => api.griffinMetadata);
  const updateNonAddressField = useStoreActions(
    ({ form }) => form.updateNonAddressField,
  );
  const isCupOtpFlow =
    hasCupOneTimePasswordConfirmationMessage && cardType === 'CHINA_UNION_PAY';
  // We show the Phone Type dropdown if isAllowChangePhoneType is true and
  // it is not a cup otp flow.
  const withPhoneType = isAllowChangePhoneType && !isCupOtpFlow;

  // Wait to render the phone input until we have the phone metadata
  if (!phoneMeta?.mask?.length) {
    return <LoadingElement />;
  }

  // Update phoneType to mobile if it is a cup otp flow as it requires
  // SMS otp confirmation.
  if (isCupOtpFlow && phoneType.value !== 'MOBILE') {
    updateNonAddressField({
      field: 'phoneType',
      payload: {
        displayError: false,
        error: '',
        errorCode: '',
        isValid: true,
        value: 'MOBILE',
      },
    });
  }

  return (
    <PhoneContainer
      canDialingCodeChange={canDialingCodeChange}
      phone={phone}
      phoneType={phoneType}
      withPhoneType={withPhoneType}
    >
      {isCupOtpFlow && <CupOtpMessage />}
      {withPhoneType && (
        <PhoneType
          canDialingCodeChange={canDialingCodeChange}
          disabled={isFormSubmitting}
          {...props}
          {...phoneType}
        />
      )}

      {canDialingCodeChange && (
        <DialingCodeSelect
          dialingCodeSelectOptions={dialingCodeSelectOptions}
          disabled={isFormSubmitting}
          {...dialingCode}
          {...props}
        />
      )}

      <PhoneInput
        {...(withPhoneType && phoneInputStyleOverrides)}
        {...phone}
        {...phoneMeta}
        {...props}
        dialingCode={territoriesToDialingCodeMap[dialingCode.value]}
        disabled={isFormSubmitting}
        displayError={!withPhoneType && phone.displayError}
        focusedClassName="is-focused"
        hasError={withPhoneType && phone.displayError}
        label={
          isCupOtpFlow
            ? getContent('fieldLabels.mobilePhone')
            : getContent('fieldLabels.phoneNumber')
        }
        maxLength={phoneMeta.mask.length}
        required={true}
      />
      {shouldShowSCAPhoneConfirmation && (
        <PhoneConfirmationText>
          {getContent('valueProps.phoneConfirmmation')}
        </PhoneConfirmationText>
      )}
    </PhoneContainer>
  );
};

Phone.propTypes = {
  blurHandler: func,
  changeHandler: func.isRequired,
  focusHandler: func,
};

export { Phone };
